const maxFileSizeMB = 50; // 50MB
const maxFileSizeErrorMessage = `We only accept files under ${maxFileSizeMB}MB in size.`;
const maxFileSizeKB = maxFileSizeMB * 1024 * 1024; // Convert to bytes

const supportedFileTypes = [
  '.csv',
  '.doc',
  '.docx',
  '.jpeg',
  '.jpg',
  '.pdf',
  '.png',
  '.ppt',
  '.pptx',
  '.txt',
  '.xls',
  '.xlsx',
];

const supportedMagicNumbers = [
  '25504446', // PDF
  'D0CF11E0', // DOC, XLS, PPT
  '504B34', // DOCX, XLSX, PPTX
  '504B0304', // DOCX, XLSX, PPTX
  'FFD8FFDB', // JPEG
  'FFD8FFE0', // JPEG
  'FFD8FFE1', // JPEG
  'FFD8FFE2', // JPEG
  'FFD8FFE3', // JPEG
  'FFD8FFE8', // JPEG
  '89504E47', // PNG
];

const showError = (callback, message) => {
  // Hide the loading indicator
  window.tinymceTopLevelWindow.unblock();

  // Display error alert
  // eslint-disable-next-line no-undef
  tinymce.activeEditor.windowManager.alert(message || 'There was an error uploading the file.');

  // Clear the file link, title and text fields
  callback('', { title: '', text: '' });
};

const generateSupportedFileTypesMessage = () => {
  const message = [
    '&#x1F7E5;&nbsp;<span style="font-weight: bold; line-height: 2;">Adobe:</span> .pdf',
    '&nbsp;&#x1F4B2;&nbsp;&nbsp;<span style="font-weight: bold; line-height: 2;">Excel:</span> .csv, .xls, .xlsx',
    '&#x1F4F7;&nbsp;<span style="font-weight: bold; line-height: 2;">Image:</span> .jpeg, .jpg, .png',
    '&#x1F4D9;&nbsp;&nbsp;<span style="font-weight: bold; line-height: 2;">PowerPoint:</span> .ppt, .pptx',
    '&#x1F4C4;&nbsp;&nbsp;<span style="font-weight: bold; line-height: 2;">Text:</span> .txt',
    '&#x1F524;&nbsp;<span style="font-weight: bold; line-height: 2;">Word:</span> .doc, .docx',
  ].join('<br>');

  return `
  <span style="font-weight: bold;">We accept the following file types, under ${Math.round(
    maxFileSizeMB,
  )}MB in size:</span>
  <br><br>
  ${message}
  `;
};

const uploadFileToS3 = (uploadConfig, blobInfo, filename, callback) => {
  const xhr = new XMLHttpRequest();
  xhr.open('PUT', uploadConfig.presigned_upload_url);

  xhr.onerror = () => {
    showError(callback);
  };

  xhr.onload = () => {
    if (xhr.status !== 200) {
      showError(callback);
      return;
    }

    // Populate the file link, title and text fields
    callback(uploadConfig.public_url, { title: filename, text: filename });

    // Hide the loading indicator
    window.tinymceTopLevelWindow.unblock();
  };

  xhr.send(blobInfo.blob());
};

// Step 1: Get Presigned URL from server
// Step 2: Upload file to S3 using Presigned URL
const fileUploadHandler = (blobInfo, filename, callback) => {
  const xhr = new XMLHttpRequest();
  const errorMessage = 'There was an error creating the upload request.';

  xhr.open('GET', `${window.fileUploadRequestUrl}&filename=${filename}&upload_type=file`);
  // Set CSRF Token
  xhr.setRequestHeader(
    'X-CSRF-TOKEN',
    document.querySelector("meta[name='csrf-token']").getAttribute('content'),
  );

  xhr.onerror = () => {
    showError(callback, errorMessage);
  };

  xhr.onload = () => {
    const response = JSON.parse(xhr.responseText);

    if (!response || !('status' in response)) {
      showError(callback, errorMessage);
    } else if (response.status === 'success') {
      uploadFileToS3(response.data, blobInfo, filename, callback);
    } else if (response.status === 'fail' && response.data.message === 'unsupported_file_type') {
      showError(callback, generateSupportedFileTypesMessage());
    } else {
      showError(callback, errorMessage);
    }
  };

  // Show a loading indicator while the file is being uploaded
  window.tinymceTopLevelWindow.block('Uploading a file...');
  xhr.send();
};

// eslint-disable-next-line no-unused-vars
const filePickerCallback = (callback, value, meta) => {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', supportedFileTypes.join(','));

  input.onchange = () => {
    const file = input.files[0];

    if (file.size > maxFileSizeKB) {
      showError(callback, maxFileSizeErrorMessage);
      return;
    }

    if (file) {
      // To check for magic numbers
      const filereader = new FileReader();
      // To upload the file
      const filereader2 = new FileReader();

      // Check for magic numbers
      filereader.onloadend = (e) => {
        const uint = new Uint8Array(e.target.result);
        const bytes = [];
        uint.forEach((byte) => {
          bytes.push(byte.toString(16));
        });
        const fileSignature = bytes.join('').toUpperCase();

        // If file magic numbers match any of the supported file types, upload the file
        // Ignore .csv and .txt files since they don't have magic numbers
        if (
          ['text/csv', 'text/plain'].includes(file.type)
          || supportedMagicNumbers.includes(fileSignature)
        ) {
          filereader2.readAsDataURL(file);
        } else {
          showError(callback, generateSupportedFileTypesMessage());
        }
      };

      // Upload file to S3 once it passes the magic number check
      filereader2.onloadend = (e) => {
        const id = `blobid-${new Date().getTime()}`;
        // eslint-disable-next-line no-undef
        const { blobCache } = tinymce.activeEditor.editorUpload;
        const base64 = e.target.result.split(',')[1];
        const blobInfo = blobCache.create(id, file, base64);

        // Register the blob in TinyMCEs image blob registry
        blobCache.add(blobInfo);

        // Perform S3 upload
        fileUploadHandler(blobInfo, file.name, callback);
      };

      filereader.readAsArrayBuffer(file.slice(0, 4));
    }
  };

  input.click();
};

export default filePickerCallback;
